import { Fragment, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLocalisation } from '@archipro-website/localisation';
import { EditorialArticleGrid } from './EditorialArticleGrid';
import { ArticleHeroPinProvider } from '~/modules-rocco/admin/components/ArticleHeroPinContext';
import { EditorialFeatureRow } from './EditorialFeatureRow';
import { EditorialViewMore } from './EditorialViewMore';
import type { MagazineEdition } from './MagazineEditionCarousel';
import { MagazineEditionCarousel } from './MagazineEditionCarousel';
import { PremiumArticleModule } from './PremiumArticleModule';
import subscribeTileImage from '../images/subscribe-tile.jpg';
import { subscribeTileBody } from '../util/editorial-copy';
import { useDirectoryViewMore } from '~/ui-rocco/pages/category-page/hooks/useProductViewMore';
import { ARTICLES_LOAD_MORE_API_ROUTE } from '~/routes/remix-api.articles.load-more';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { EDITORIAL_GROUND } from '../config/editorial-ground';
import {
    EDITORIAL_RAIL,
    EditorialDirectoryOpening,
} from './EditorialDirectoryOpening';
import { EditorialFilterBar } from './EditorialFilterBar';
import { DigitalPublicationsRow } from './DigitalPublicationsRow';
import type { DigitalPublicationOption } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import { cn } from '@rocco/utils/cn';
import type { CategoryNavItem } from '../util/derive-category-rail';
import { deriveCategoryRail } from '../util/derive-category-rail';
import type { ArchizeenTile } from '../util/map-archizeen-tile-to-editorial';
import { mapArchizeenTileToEditorial } from '../util/map-archizeen-tile-to-editorial';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import { useArticlesFilterSort } from '../hook/use-articles-filter-sort';
import { articlesRevampFiltersConfig } from '../config/filter.config';
import type { ThemeFacetOption } from '../util/theme-facets';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { DirectoryFilterSidebar } from '~/ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import { buildOrderGroup } from '../util/order-groups';
import { EditorialEmptyResults } from './EditorialEmptyResults';
import {
    DirectoryFilterDrawerProvider,
    useDirectoryFilterOpenState,
} from '~/ui-rocco/pages/directory-browse/directory-filter-drawer';

interface ArticleCategoryRevampProps {
    articles: ArchizeenTile[];
    /** Total across all pages, so the grid knows when to stop paging. */
    totalCount: number;
    /** The page size the loader served — the films category serves 18, not 36. */
    pageSize?: number;
    /** Latest issue of each magazine series, for the edition carousel. */
    editions?: MagazineEdition[];
    navItems: CategoryNavItem[];
    /**
     * The directory's statement, which this page opens on too — the same
     * words, so a tab changes the grid and nothing above it.
     */
    statement: string;
    /** Facet states for the listing, from the route loader. */
    filterStates: FilterStates;
    /** The editorial theme vocabulary for this market, grouped into the sidebar. */
    themes?: ThemeFacetOption[];
    /**
     * Articles pinned to the /articles hero. A pick is global to the
     * directory, so a tile here pins to the same carousel; this only labels
     * the tile's own menu Pin or Unpin.
     */
    heroPinnedArticleIDs?: number[];
    /** The digital publications for the row under the opening, in catalogue order. */
    publications?: DigitalPublicationOption[];
    /**
     * Shows the rail's filter chip and mounts the shared bottom sheet — the
     * category pages' mobile filter pattern. Decided by the route from the
     * device, so the desktop rail keeps its chipless composition.
     */
    showMobileFilters?: boolean;
}

/**
 * How the listing repeats: three rows of the grid's rhythm, then one article
 * given the whole width.
 *
 * Ten is `EditorialArticleGrid`'s own run — a double and two singles, two
 * singles and a double, then four singles — so a grid handed exactly ten cards
 * draws those three rows and stops. The eleventh article is the feature, which
 * makes the fourth row. Both counts have to stay in step with `RUN_LENGTH`
 * there.
 */
const RHYTHM_CARDS = 10;
const RUN_CARDS = RHYTHM_CARDS + 1;

/** Splits the listing into runs of the four-row pattern, in source order. */
const toRuns = <T,>(items: T[], size: number): T[][] => {
    const runs: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        runs.push(items.slice(i, i + size));
    }
    return runs;
};

/**
 * Composes the revamped /articles/{category} template.
 *
 * The directory's own page with the grid narrowed: the same opening, the same
 * rail with a different pill on, and the listing under it. Nothing above the
 * grid moves between the directory and a theme, or between two themes.
 */
export const ArticleCategoryRevamp = ({
    articles,
    totalCount,
    pageSize,
    editions = [],
    navItems,
    statement,
    filterStates,
    themes = [],
    heroPinnedArticleIDs = [],
    publications = [],
    showMobileFilters = false,
}: ArticleCategoryRevampProps) => {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { country } = useLocalisation();
    // The theme dimensions are editorial data, so the facet config is built per
    // request rather than declared.
    const filtersConfig = useMemo(
        () => articlesRevampFiltersConfig(themes),
        [themes],
    );
    const { searchFilters } = useArticlesFilterSort(
        filterStates,
        filtersConfig,
    );
    // Persisted per directory, so the panel survives a trip into an article and
    // back — the same rule the other browse pages follow.
    const { toggle: toggleFilters } = useDirectoryFilterOpenState('articles');
    const orderGroup = useMemo(
        () => buildOrderGroup(pathname, search),
        [pathname, search],
    );

    const rail = useMemo(
        () => deriveCategoryRail(navItems, pathname),
        [navItems, pathname],
    );

    const initialTiles = useMemo(
        () =>
            articles.map((article) => ({
                model: mapArchizeenTileToEditorial(article),
            })),
        [articles],
    );

    // The query string carries the active facets, so paging has to inherit it —
    // otherwise page two would arrive unfiltered. It travels through the hook's
    // `filters` slot, which also makes it part of the hook's result-set
    // identity: a facet change resets the accumulated pages, as it should.
    const filters = useMemo(() => ({ query: search }), [search]);

    const {
        products: paged,
        loadMore,
        hasMore,
        isLoading,
        nextPage,
    } = useDirectoryViewMore({
        initialProducts: initialTiles,
        totalCount,
        categoryLink: pathname,
        pageSize,
        filters,
        actionPath: ARTICLES_LOAD_MORE_API_ROUTE,
    });

    const tiles = useMemo(
        () => paged.map((tile) => tile.model as ArticleTileViewModel),
        [paged],
    );

    return (
        <ArticleHeroPinProvider pinnedArticleIDs={heroPinnedArticleIDs}>
            <main
                // `relative`, so the wordmark's absolute box resolves against
                // this column; overflow-x-clip contains the rail's full-bleed
                // breakout — see the directory page for why 100vw overhangs.
                className={cn(
                    'relative flex flex-col overflow-x-clip pb-10 md:pb-[10rem]',
                    EDITORIAL_GROUND,
                )}
                data-testid="article-category-revamp"
            >
                <EditorialDirectoryOpening statement={statement} />
                {/* The publications under the opening, as the directory
                    root sets them, so a tab changes the grid and nothing
                    above it. */}
                {/* Below md the covers step after the rail (order-1; the grid
                    and closers at order-2..4), so the pills sit directly under
                    the opening the way every category page's do. md+ keeps
                    the Figma order. */}
                <DigitalPublicationsRow
                    publications={publications}
                    hideIntro
                    className="order-1 mt-10 mb-0 md:order-none md:mt-[4.0625rem] md:mb-[8.125rem]"
                />
                {/* Desktop keeps the chipless rail — a control at its head
                    would push every pill along by its own width the moment a
                    tab is clicked, and the facets stay reachable through the
                    query string. Phones get the category pages' filter chip
                    and bottom sheet instead: hasSidebar turns on the rail's
                    mobile Refine pill and the sheet DirectoryPillBar mounts
                    with it. */}
                <DirectoryFilterDrawerProvider
                    value={{
                        open: false,
                        toggle: toggleFilters,
                        hasSidebar: showMobileFilters,
                    }}
                >
                    <EditorialFilterBar
                        items={rail.items}
                        active={rail.active}
                        searchFilters={searchFilters}
                        // The directory's rail, to the class: the same measure,
                        // band, pills and rule, so the only thing a tab changes
                        // up here is which pill is on. No crumb trail either —
                        // the pill names the page.
                        {...EDITORIAL_RAIL}
                        // The rail's px-1 mobile gutter compensates for the
                        // pill scroller's internal padding on a chipless row;
                        // with the filter chip leading, the chip itself must
                        // land on the 16px gutter the way the category pages'
                        // does.
                        {...(showMobileFilters && {
                            gutterClassName:
                                'px-4 md:px-[max(3rem,calc((100%_-_1920px)/2_+_3rem))]',
                        })}
                        showSectionHeading={false}
                    />
                    <CollapsiblePageLayout
                        sidePanel={
                            <DirectoryFilterSidebar
                                searchFilters={searchFilters}
                                sortOptions={[]}
                                hiddenSectionTitles={[]}
                                leadingGroups={[orderGroup]}
                                // Three short sections: collapsing all but the
                                // first would hide most of what can be filtered.
                                expandAllSections
                            />
                        }
                        hideSidePanel
                        disableMinHeight
                        paddingClassName="px-0"
                        className="order-2 mt-10 md:order-none md:mt-0"
                    >
                        {/*
                         * No top margin on the grid: the filter bar above closes
                         * with its own padding and the products, projects and
                         * professionals listings all start their grid on it. The
                         * empty state keeps a little, being a line of text rather
                         * than a row of images.
                         */}
                        {tiles.length === 0 ? (
                            <EditorialEmptyResults
                                className="mt-10 md:mt-[3rem]"
                                // Straight to the bare category: the facets all
                                // live in the query string, so dropping it is the
                                // whole reset — and it keeps the reader on the
                                // category they were reading.
                                onClearFilters={() =>
                                    navigate(pathname, {
                                        preventScrollReset: true,
                                    })
                                }
                            />
                        ) : (
                            toRuns(tiles, RUN_CARDS).map((run, runIndex) => {
                                const feature = run[RHYTHM_CARDS];
                                return (
                                    <Fragment key={run[0]?.id ?? runIndex}>
                                        <EditorialArticleGrid
                                            articles={run.slice(
                                                0,
                                                RHYTHM_CARDS,
                                            )}
                                            hideStandfirst
                                            graded
                                            // The grid opens at the fold, so
                                            // the first two rows load with the
                                            // page, as the browse grids' do.
                                            eagerCount={runIndex === 0 ? 6 : 0}
                                            // The first run starts on the filter
                                            // bar's own padding, as the other
                                            // listings do; later runs open below a
                                            // feature row and need the gap.
                                            className={
                                                runIndex === 0
                                                    ? undefined
                                                    : 'mt-10 md:mt-[5rem]'
                                            }
                                        />
                                        {feature ? (
                                            <EditorialFeatureRow
                                                article={feature}
                                                className="mt-10 md:mt-[5rem]"
                                            />
                                        ) : null}
                                    </Fragment>
                                );
                            })
                        )}

                        <EditorialViewMore
                            hasMore={hasMore}
                            isLoading={isLoading}
                            onLoadMore={loadMore}
                            nextPage={nextPage}
                            className="mt-10 md:mt-[4.9375rem]"
                        />
                    </CollapsiblePageLayout>
                </DirectoryFilterDrawerProvider>

                {/*
                 * Figma rhythm for the closing sections: 190 to the subscribe
                 * module, 160 to the magazine carousel.
                 *
                 * Explore more sits between them in the design and is built, but is
                 * not mounted: its cards are drawn as full-bleed category
                 * photographs and nothing supplies one, so they render as three
                 * black boxes carrying only a label. It goes back in once a
                 * category can be asked for an image to stand behind its name.
                 */}

                {/* The same module as the directory's premium feature, carrying
                subscribe copy — which is what the design does here. */}
                {/*
                 * A standing promotion, not an article — so its image is the
                 * design's own asset rather than whatever the category happens to
                 * hold. Copy is the design's, which wraps to four lines in the 805
                 * column at 33px.
                 */}
                <PremiumArticleModule
                    article={{
                        title: 'Want more of this delivered to your inbox?',
                        link: '/digital-magazines',
                        intro: subscribeTileBody(country),
                        imageSrc: subscribeTileImage,
                    }}
                    eyebrow="Digital publication"
                    ctaLabel="Subscribe (Free)"
                    variant="subscribe"
                    imageBundled={true}
                    className="order-3 mt-20 md:order-none md:mt-[11.875rem]"
                />

                <MagazineEditionCarousel
                    editions={editions}
                    className="order-4 mt-20 md:order-none md:mt-[10rem]"
                />
            </main>
        </ArticleHeroPinProvider>
    );
};
